import { useEffect, useState } from "react";
import { SelectItem } from "@/components/ui/select";
import api from "@/lib/api";
import { refIneligibleReason } from "@/lib/refereeEligibility";

const hhmm = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const CONCURRENT_RE = /^\s*(league[\s-]*assigned|parent(\s+(helper|ref(eree)?))?)\s*$/i;
const allowsConcurrent = (r) => !!r.allow_concurrent || CONCURRENT_RE.test(r.name || "");

// Find the referee's clashing assignment (if any) for the given window, ignoring one booking id.
export function refereeBusyAt(busy, refId, start, end, ignoreBookingId) {
  if (start == null || end == null) return null;
  return (busy || []).find(b => b.referee_id === refId && b.booking_id !== ignoreBookingId && b.start < end && b.end > start) || null;
}

export function bandFor(dateStr, startMin) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const dow = d.getDay();
  if (dow !== 0 && dow !== 6) return null;
  const sat = new Date(d); if (dow === 0) sat.setDate(sat.getDate() - 1);
  return { weekend: sat.toISOString().slice(0, 10), band: `${dow === 6 ? "sat" : "sun"}_${(startMin ?? 0) < 720 ? "am" : "pm"}` };
}

export function useRefAvailability(enabled = true) {
  const [data, setData] = useState(null);
  useEffect(() => { if (enabled) api.get("/referees/availability").then(r => setData(r.data)).catch(() => {}); }, [enabled]);
  return data;
}

export function AvailChip({ entry, askedAt, compact }) {
  const st = entry?.status || "no_reply";
  const cls = st === "available" ? "border-[#83B68B]/60 text-[#83B68B] bg-[#83B68B]/10" : st === "unavailable" ? "border-red-400/60 text-red-300 bg-red-500/10" : "border-border opacity-60";
  const label = st === "available" ? "Available" : st === "unavailable" ? "Not available" : "No reply";
  const title = entry?.source === "coach" ? `Set by ${entry.set_by_name}` : entry?.source === "booking" ? "Set automatically by a game assignment" : entry?.at ? `Answered ${entry.at.slice(0, 10)}` : askedAt ? `Asked ${askedAt.slice(0, 10)}` : "Not asked yet";
  return <span title={title} className={`text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border ${cls}`} data-testid={`avail-chip-${st}`}>{label}{!compact && entry?.source === "coach" ? ` · ${entry.set_by_name}` : ""}</span>;
}

export function useLeagues(enabled = true) {
  const [data, setData] = useState([]);
  useEffect(() => { if (enabled) api.get("/leagues").then(r => setData(r.data || [])).catch(() => {}); }, [enabled]);
  return data;
}

export function RefereeSelectItems({ referees, busy, start, end, ignoreBookingId, emptyText, date, isAdmin, team }) {
  const avail = useRefAvailability(!!date);
  const leagues = useLeagues(!!team?.league_id);
  const league = team?.league_id ? leagues.find(l => l.id === team.league_id) : null;
  const list = (referees || []).filter(r => r && r.id);
  if (list.length === 0) return <div className="px-3 py-2 text-xs opacity-50">{emptyText}</div>;
  const slot = bandFor(date, start);
  return list.map(r => {
    const placeholder = allowsConcurrent(r);
    const clash = placeholder ? null : refereeBusyAt(busy, r.id, start, end, ignoreBookingId);
    const reason = placeholder ? null : refIneligibleReason(r, { teamAge: team?.age_group, league, ymd: date });
    const aref = avail?.referees?.find(x => x.id === r.id);
    const entry = slot && aref ? aref.availability?.[slot.weekend]?.[slot.band] : null;
    const notAvail = !placeholder && entry?.status === "unavailable";
    const disabled = !!clash || ((notAvail || !!reason) && !isAdmin);
    const greyed = !!clash || notAvail || !!reason;
    return (
      <SelectItem key={r.id} value={r.id} disabled={disabled} data-testid={`referee-option-${r.id}`} data-ineligible={greyed ? "true" : undefined} className={greyed ? "opacity-50" : ""}>
        {r.name}{r.is_qualified ? " · Qualified" : ""}{!reason && r.max_age_group ? ` · up to ${r.max_age_group}` : ""}
        {slot && !placeholder && aref && <> {" "}<AvailChip entry={entry} askedAt={aref.avail_asked_at} compact /></>}
        {clash && <span className="text-amber-400"> · busy {hhmm(clash.start)}–{hhmm(clash.end)}{clash.pitch_name ? ` at ${clash.pitch_name}` : ""}{clash.team_name ? ` (${clash.team_name})` : ""}</span>}
        {!clash && reason && <span className="text-amber-400" data-testid={`referee-reason-${r.id}`}> · {reason}{isAdmin ? " (admin override)" : ""}</span>}
      </SelectItem>
    );
  });
}
